// @ts-nocheck
import { useState, useEffect } from 'react';
import { Download, FileText } from 'lucide-react';
import { StatsCard } from '../components/StatsCard';
import { api } from '../lib/api';
import { useYears } from '../lib/useYears';
import { formatCurrency } from '../lib/utils';

export function Reports() {
  const { years, year, setYear } = useYears();
  const [summary, setSummary] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!year) return;
    setLoading(true);
    api.reportSummary(year).then(res => {
      setSummary(res.summary as Record<string, unknown>);
    }).finally(() => setLoading(false));
  }, [year]);

  const handleExport = async (status?: string) => {
    try {
      const csv = await api.exportCsv(year as number, status);
      const blob = new Blob([csv as string], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `wht_report_${year}${status ? `_${status}` : ''}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(String((err as Error).message));
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">Loading...</div>;

  const s = summary || {};

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Reports</h1>
        <select value={year ?? ""} onChange={e => setYear(Number(e.target.value))} className="border rounded-lg px-3 py-2 text-sm">
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard title="Total WHT" value={formatCurrency(Number(s.total_amount || 0))} subtitle={`${s.total_transactions || 0} transactions from ${s.total_clients || 0} clients`} />
        <StatsCard title="Collected" value={formatCurrency(Number(s.collected_amount || 0))} subtitle={`${s.collected_count || 0} certificates`} color="text-green-600" />
        <StatsCard title="Missing" value={formatCurrency(Number(s.missing_amount || 0))} subtitle={`${s.missing_count || 0} certificates`} color="text-red-600" />
        <StatsCard title="Verified (Ready to File)" value={formatCurrency(Number(s.verified_amount || 0))} subtitle={`${s.verified_count || 0} certificates`} color="text-blue-600" />
      </div>

      {/* Waived */}
      {Number(s.waived_count || 0) > 0 && (
        <div className="bg-gray-50 rounded-lg border p-4 text-sm">
          <span className="text-gray-500">Waived:</span>{' '}
          <span className="font-medium">{s.waived_count as number} certificates</span> totaling{' '}
          <span className="font-medium">{formatCurrency(Number(s.waived_amount || 0))}</span>
        </div>
      )}

      {/* Export Section */}
      <div className="bg-white rounded-lg border p-5">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <FileText size={18} /> Export Data
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <button onClick={() => handleExport()} className="flex items-center justify-center gap-2 px-4 py-3 border rounded-lg text-sm hover:bg-gray-50">
            <Download size={16} /> All Transactions
          </button>
          <button onClick={() => handleExport('missing')} className="flex items-center justify-center gap-2 px-4 py-3 border border-red-200 text-red-600 rounded-lg text-sm hover:bg-red-50">
            <Download size={16} /> Missing Only
          </button>
          <button onClick={() => handleExport('verified')} className="flex items-center justify-center gap-2 px-4 py-3 border border-green-200 text-green-600 rounded-lg text-sm hover:bg-green-50">
            <Download size={16} /> Verified (Filing Ready)
          </button>
          <button onClick={() => handleExport('requested')} className="flex items-center justify-center gap-2 px-4 py-3 border border-amber-200 text-amber-600 rounded-lg text-sm hover:bg-amber-50">
            <Download size={16} /> Requested / Pending
          </button>
        </div>
      </div>

      {/* Summary Table */}
      <div className="bg-white rounded-lg border">
        <div className="p-4 border-b">
          <h3 className="font-semibold">Year {year} Summary</h3>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-4 py-3 text-left text-xs text-gray-500 uppercase">Category</th>
              <th className="px-4 py-3 text-right text-xs text-gray-500 uppercase">Count</th>
              <th className="px-4 py-3 text-right text-xs text-gray-500 uppercase">Amount</th>
              <th className="px-4 py-3 text-right text-xs text-gray-500 uppercase">% of Total</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {[
              { label: 'Total', count: s.total_transactions, amount: s.total_amount, cls: 'font-bold' },
              { label: 'Verified', count: s.verified_count, amount: s.verified_amount, cls: 'text-green-600' },
              { label: 'Collected (pending verification)', count: s.collected_count, amount: s.collected_amount, cls: 'text-blue-600' },
              { label: 'Missing / Requested', count: s.missing_count, amount: s.missing_amount, cls: 'text-red-600' },
              { label: 'Waived', count: s.waived_count, amount: s.waived_amount, cls: 'text-gray-400' },
            ].map(row => (
              <tr key={row.label} className="hover:bg-gray-50">
                <td className={`px-4 py-3 ${row.cls}`}>{row.label}</td>
                <td className={`px-4 py-3 text-right ${row.cls}`}>{Number(row.count || 0)}</td>
                <td className={`px-4 py-3 text-right font-mono ${row.cls}`}>{formatCurrency(Number(row.amount || 0))}</td>
                <td className="px-4 py-3 text-right text-gray-500">
                  {Number(s.total_amount) > 0 ? `${Math.round((Number(row.amount || 0) / Number(s.total_amount)) * 100)}%` : '0%'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
